import { BonusCondition, ConditionMode, Currency, getUnitTranslationId } from '@synergy/models';
import { FormattedMessage, useIntl } from 'react-intl';
import { InformationOutlinedIcon, theme } from '@synergy/theme';
import { PoolTooltip } from '@synergy/components-next';
import { Stack, TableCell, styled } from '@mui/material';
import { getCurrencySymbol } from '@synergy/utils';

interface BonusConditionTableCellHeaderProps {
    bonusCondition: BonusCondition;
    defaultCurrency: Currency;
    locale: string;
}

const StyledStack = styled(Stack)(({ theme }) => ({
    flexDirection: 'row',
    gap: theme.spacing(0.5),
    alignItems: 'center',
    justifyContent: 'flex-end',
}));

export const BonusConditionTableCellHeader = ({
    bonusCondition,
    defaultCurrency,
    locale,
}: BonusConditionTableCellHeaderProps) => {
    const intl = useIntl();
    const { id, mode, unit, currency } = bonusCondition;
    const currencySymbol = getCurrencySymbol(currency || defaultCurrency, locale);

    return (
        <>
            <TableCell key={id} sx={{ minWidth: 70, maxWidth: 150, textAlign: 'right' }}>
                <StyledStack>
                    <FormattedMessage
                        id={`app.bonusSettlementSupplier.bonusSettlementCondition.${mode}`}
                        values={{
                            mode,
                            quantity: bonusCondition.quantity || 1,
                            percentage: intl.formatNumber((bonusCondition.percentage ?? 0) * 100, {
                                maximumFractionDigits: 2,
                            }),
                            price: intl.formatNumber(bonusCondition.price ?? 0, {
                                maximumFractionDigits: 2,
                                minimumFractionDigits: 2,
                            }),
                            currency: currencySymbol,
                            unit:
                                unit &&
                                intl.formatMessage({
                                    id: getUnitTranslationId(unit),
                                }),
                        }}
                    />
                    {bonusCondition.title && (
                        <PoolTooltip title={bonusCondition.description || bonusCondition.title}>
                            <InformationOutlinedIcon htmlColor={theme.palette.primary.main} sx={{ fontSize: 16 }} />
                        </PoolTooltip>
                    )}
                </StyledStack>
            </TableCell>
            {mode === ConditionMode.Amount && (
                <TableCell
                    sx={{ maxWidth: 140, textAlign: 'right', borderRight: `1px solid ${theme.palette.grey[700]}` }}
                >
                    <FormattedMessage id="app.table.column.revenue" />
                </TableCell>
            )}
        </>
    );
};
